import React, { useEffect } from "react";
import { Mail, PhoneCall, MapPin, Clock, Globe } from "lucide-react";
import { ContactForm } from "../components/forms/ContactForm";
import { SITE } from "../data/content";

const Contact = () => {
  useEffect(() => { document.title = "Contact | Iron Shield Veterans Reset Center"; }, []);

  return (
    <div className="min-h-screen bg-offwhite pb-20" data-testid="contact-page">
      {/* Header banner */}
      <section className="bg-navy text-offwhite pt-[130px] md:pt-[150px] pb-12 border-b-4 border-gold">
        <div className="container-x">
          <div className="max-w-3xl space-y-3">
            <div className="flex items-center gap-3">
              <div className="h-px w-8 bg-gold" />
              <span className="text-gold text-xs font-bold uppercase tracking-[0.2em]">Get In Touch</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-black tracking-tight">Contact & Outreach Center</h1>
            <p className="text-offwhite/80 text-sm sm:text-base leading-relaxed">
              Have a question about volunteering, corporate partnerships, or general nonprofit operations? Reach out directly below. For veteran intake or referrals, please use the dedicated forms.
            </p>
          </div>
        </div>
      </section>

      <div className="container-x pt-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left: contact info */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white p-6 sm:p-8 rounded-2xl border border-softgray brand-card-shadow space-y-6" data-testid="contact-info-card">
              <h3 className="text-xl font-bold text-navy border-b border-softgray pb-3">Main Outreach Facility</h3>
              <div className="space-y-4 text-sm text-charcoal">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-navy">Campus</div>
                    <div className="text-slate">{SITE.campus}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <PhoneCall className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-navy">Intake & Emergency Line</div>
                    <a href={`tel:${SITE.hotline}`} className="text-slate font-semibold hover:text-navy transition-colors">{SITE.hotline}</a>
                    <div className="text-[11px] text-slate">24/7 Operations Duty Officer</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-navy">Inquiry Email</div>
                    <a href={`mailto:${SITE.email}`} className="text-slate hover:text-navy transition-colors" data-testid="contact-email-link">{SITE.email}</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-navy">Office Administrative Hours</div>
                    <div className="text-slate">Monday – Friday: 8:00 AM – 6:00 PM EST</div>
                    <div className="text-slate">Saturday & Sunday: Residential Duty Only</div>
                  </div>
                </div>
              </div>
              <div className="bg-navy text-offwhite p-4 rounded-xl text-xs space-y-1">
                <div className="font-bold text-gold">National Veterans Crisis Line</div>
                <p>In immediate danger? Dial <strong>988 (Press 1)</strong> for free 24/7 crisis support.</p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-softgray brand-card-shadow space-y-3" data-testid="contact-service-area">
              <div className="flex items-center gap-2 text-navy font-bold text-base">
                <Globe className="w-5 h-5 text-gold" /> Service Area Coverage
              </div>
              <p className="text-xs text-slate leading-relaxed">
                {SITE.serviceArea} — including Winston-Salem, Greensboro, Durham, Raleigh, Charlotte, and Fayetteville — and we accept out-of-state referrals from regional VA medical centers.
              </p>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-2xl p-6 sm:p-10 border border-softgray shadow-md">
              <h3 className="text-xl font-bold text-navy pb-3 border-b border-softgray mb-6">General Contact & Inquiry Form</h3>
              <ContactForm />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
